import React, {
    type Dispatch,
    type SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { Dialog } from '../ui/dialog'
import { Switch } from '../ui/switch'
import { Button } from '../ui/button'
import { useSelector, useDispatch } from 'react-redux'
import { Filters, RecipeTrigger, TableName } from '../../config'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { TextArea, type BreadcrumbProps } from '@blueprintjs/core'
import { Menu, MenuItem } from '../ui/menu'
import { Layout } from '../Layout'
import type { LoadFunc, Params } from '../../packages/blueprint-components/src/useTableHooks'
import { UpdateReportAdTypeBadge } from '../badge/UpdateReportAdTypeBadge'
import { ActionBadge } from './Actions'
import { FrequencyBadge, MerchJarAPI, MerchJarPublicRecipesAPI } from '../../pages/recipe_detail'
import { Toasts } from '../toaster/Toast'
import Recipe = Models.Recipe
import { axiosGet, axiosPatch, axiosPost, axiosDestroy } from '../../util/api'
import { format } from 'date-fns'
import type { EntityId } from '../../types/constants'
import { setRecipeProgress as reduxSetRecipesProgress } from '../../actions/progress'
import { useInterval } from '../../hooks/useInterval'
import { snakeCase } from 'change-case'
import { useUser } from '../../hooks/useUser'
import { Popover } from '../ui/popover'
import { fetchRecipeProgress, type State } from '../../reducers'
import { isNavV2Enabled } from '../../util/nav-version'
import { PreservingLink } from '../ui/PreservingLink'
import { EllipsisMenu } from '../../icons/icons'
import type { BulkActionProgress, RecipeProgressList } from '../../types/reducers/progress'
import { ProgressPreview } from '../BulkActionsPreviewDialog'
import type { Column } from '../../types/table'
import classNames from 'classnames'
import {
    ScheduledSegmentFrequency,
    type ScheduledSegmentListRecipe,
} from '../../features/scheduled-segments/components/ScheduledSegmentListPresentation'
import { RecipeListAgGrid, useRecipeListTable } from './RecipeListAgGrid'
import { RecipeUpgradeBanner } from './RecipeUpgradeBanner'
import { RecipesRetiredNotice } from './RecipesRetiredNotice'
import { recipesRetiredCopy } from '../../features/recipe-upgrades/retired-copy'
import { recipeUpgradeListCopy } from '../../features/recipe-upgrades/list-copy'
import { toastUpgradedToV2Refusal } from '../../features/recipe-upgrades/enable-refusal'
import {
    getRecipeUpgrades,
    getRecipeUpgradeSummary,
} from '../../features/recipe-upgrades/list-client'
import type {
    UpgradeListSummary,
    UpgradeReviewRow,
    UpgradeVerdictStatus,
} from '../../features/recipe-upgrades/list-types'

const filters = [
    Filters.RecipeEnabled,
    Filters.RecipeAdTypes,
    Filters.RecipeActions,
    Filters.RecipeFrequencies,
]

export const recipeFilters = (withUpgradeStatus: boolean) =>
    withUpgradeStatus ? [...filters, Filters.RecipeUpgradeStatus] : filters

interface RecipesTableProps {
    reloadParent?: any
    targetId?: any
    targetType?: any
    showHeader?: boolean
    nested?: boolean
    asPublicRecipes?: boolean
}

export const UpgradedRowMarker = ({
    segmentId,
    upgradedAt,
}: {
    segmentId: string | null
    upgradedAt: string | null
}) => (
    <div className="mt-1 flex items-center gap-2 text-xs leading-4">
        <Popover
            interactionKind="hover"
            content={
                <div className="max-w-[340px] bg-[#1C2127] px-3 py-2.5 text-white">
                    {recipeUpgradeListCopy.upgradedTooltip(
                        upgradedAt ? format(new Date(upgradedAt), 'd MMMM') : '',
                    )}
                </div>
            }
        >
            <span className="rounded-sm bg-[rgb(35_133_81_/_10%)] px-1.5 py-0.5 text-[#1C6E42]">
                {recipeUpgradeListCopy.upgraded}
            </span>
        </Popover>
        <Link
            className="text-[#215DB0] hover:text-[#184A90] hover:underline focus:text-[#184A90] focus:underline"
            to={segmentId ? `/segments/${segmentId}` : '/segments'}
        >
            View Segment
        </Link>
    </div>
)

export const RecipeUpgradeCell = ({
    recipe,
    status,
    enabled,
}: {
    recipe: Recipe
    status: UpgradeVerdictStatus | undefined
    enabled: boolean
}) => {
    if (!enabled || recipe.version !== 1) {
        return null
    }

    if (status === 'upgraded') {
        return <UpgradedRowMarker segmentId={null} upgradedAt={null} />
    }

    if (status === 'unparseable') {
        return (
            <span
                className="rounded-sm bg-[rgba(143,153,168,.15)] px-[6px] py-[2px] text-[12px] leading-4 text-[#5f6b7c]"
                title={recipeUpgradeListCopy.cannotUpgradeTooltip}
            >
                {recipeUpgradeListCopy.cannotUpgrade}
            </span>
        )
    }

    return (
        <Link
            className="inline-flex min-h-[30px] items-center rounded-lg bg-[#2d72d2] px-4 py-[5px] text-[14px] leading-5 text-white shadow-[0_2px_4px_rgba(0,0,0,.075)]"
            to={`/recipes/upgrade-to-segments?recipe_id=${String(recipe.id)}`}
        >
            {recipeUpgradeListCopy.upgrade}
        </Link>
    )
}

export const RecipesTable = ({
    reloadParent,
    showHeader = true,
    nested = false,
    asPublicRecipes = false,
}: RecipesTableProps) => {
    const dispatch = useDispatch()
    const { user } = useUser()
    const [loading, setLoading] = useState(false)
    const [recipe, setRecipe] = useState<Models.Recipe | null>(null)
    const recipesProgress = useSelector<State, RecipeProgressList>(
        (store) => store.progress.recipes,
    )
    const profile = useSelector<State, EntityId | null>((state) => state.user.profile)
    const navigate = useNavigate()
    const { search } = useLocation()
    const useNavV2 = useMemo(() => isNavV2Enabled(search), [search])
    const useNavV2FlatChrome = useNavV2 && !nested
    const [description, setDescription] = useState<Models.Recipe | null>(null)
    const API = asPublicRecipes ? MerchJarPublicRecipesAPI : MerchJarAPI
    const [upgradeSummary, setUpgradeSummary] = useState<UpgradeListSummary | null>(null)
    const [upgradeRows, setUpgradeRows] = useState<UpgradeReviewRow[]>([])
    const upgradeSummaryErrorLoggedRef = useRef(false)

    const load: LoadFunc<Recipe> = useCallback(
        (params: any) => {
            if (asPublicRecipes ? !user?.is_admin : !profile && !user?.is_admin) {
                return Promise.resolve({
                    data: {
                        data: [],
                    },
                })
            }

            if (reloadParent) {
                const { from, to }: Params = { ...params }
                reloadParent({ from, to })
            }

            return API.Recipes.Browse(params)
        },
        [API, asPublicRecipes, profile, reloadParent, user?.is_admin],
    )

    const { store, savedFiltersStore, tableHooks } = useRecipeListTable(
        asPublicRecipes ? TableName.PublicRecipes : TableName.Recipes,
        load,
        `${asPublicRecipes}:${profile ?? ''}`,
    )

    useEffect(() => {
        if (asPublicRecipes) {
            return
        }

        setUpgradeSummary(null)
        setUpgradeRows([])
        // The ProfileId header comes from the user reducer; without it the server answers for
        // the account's default profile.
        if (!profile) {
            return
        }
        void getRecipeUpgradeSummary()
            .then((summary) => {
                setUpgradeSummary(summary)
                return getRecipeUpgrades()
                    .then(setUpgradeRows)
                    .catch(() => setUpgradeRows([]))
            })
            .catch((error) => {
                setUpgradeSummary(null)
                setUpgradeRows([])

                if (!upgradeSummaryErrorLoggedRef.current) {
                    upgradeSummaryErrorLoggedRef.current = true
                    console.error('Failed to load recipe upgrade summary', error)
                }
            })
    }, [asPublicRecipes, profile])

    const recipesCurrentlyRunning = useMemo(
        () => Object.keys(recipesProgress).length > 0,
        [JSON.stringify(recipesProgress)],
    )

    useEffect(() => {
        tableHooks.reload()
    }, [recipesCurrentlyRunning])

    useInterval(() => {
        // @ts-expect-error
        dispatch(fetchRecipeProgress()).then((finishedUuids: string[]) => {
            finishedUuids.forEach((uuid) => {
                axiosGet('recipes/run-info', { uuid })
                    .then((res) => {
                        Toasts.success(res.data.message)
                        console.log('recipes run success', res.data)
                    })
                    .catch((err) => {
                        console.log('recipes fetch error', err)
                    })
            })
        })
    }, 1000)

    const duplicateRecipe = ({ recipe }: { recipe: Models.Recipe }) => {
        axiosPost(
            (asPublicRecipes ? 'admin/public-recipes' : 'recipes') + `/${recipe.id}/duplicate`,
        )
            .then(() => {
                Toasts.success('Recipe successfully duplicated')
                tableHooks.reload()
            })
            .catch(() => {
                Toasts.error('Failed to duplicate recipe')
            })
    }

    const exportRecipe = ({ recipe }: { recipe: Models.Recipe }) => {
        axiosGet(
            (asPublicRecipes ? 'admin/public-recipes' : 'recipes') + `/${recipe.id}/export`,
        ).then((r) => {
            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([r.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', snakeCase(recipe.name) + `.recipe`)

            // Append to html link element page
            document.body.appendChild(link)

            // Start download
            link.click()

            // Clean up and remove the link
            link.parentNode?.removeChild(link)

            Toasts.success('Recipe exported')
        })
    }

    const deleteRecipe = ({ recipe }: { recipe: Models.Recipe }) => {
        if (asPublicRecipes) {
            // Public recipes don't have individual delete endpoint, use bulk delete
            axiosPatch('admin/public-recipes/bulk', {
                selection: { selected: [recipe.id], selectedCount: 1 },
                update: { recipes_delete: true },
            })
                .then(() => {
                    Toasts.success('Recipe deleted')
                    tableHooks.reload()
                })
                .catch(() => {
                    Toasts.error('Failed to delete recipe')
                })
        } else {
            axiosDestroy(`recipes/${recipe.id}`)
                .then(() => {
                    Toasts.success('Recipe deleted')
                    tableHooks.reload()
                })
                .catch(() => {
                    Toasts.error('Failed to delete recipe')
                })
        }
    }

    const upgradeStatuses = useMemo(
        () => new Map(upgradeRows.map((row) => [row.recipe_id, row.status])),
        [upgradeRows],
    )
    const upgradeRowsById = useMemo(
        () => new Map(upgradeRows.map((row) => [row.recipe_id, row])),
        [upgradeRows],
    )

    const columns = useMemo(() => {
        const c: Column<Recipe, any>[] = [
            {
                key: 'name',
                cell: (recipe) => {
                    const upgrade = upgradeRowsById.get(String(recipe.id))
                    return (
                        <div>
                            <PreservingLink
                                to={
                                    asPublicRecipes
                                        ? `/admin/public-recipes/${recipe?.id}`
                                        : `/recipes/${recipe?.id}`
                                }
                            >
                                {recipe?.name}
                            </PreservingLink>
                            {upgrade?.status === 'upgraded' && (
                                <UpgradedRowMarker
                                    segmentId={upgrade.segment_id}
                                    upgradedAt={upgrade.upgraded_at}
                                />
                            )}
                        </div>
                    )
                },
            },
            {
                key: 'enabled',
                cell: (recipe, extra: any) => {
                    const upgraded = upgradeRowsById.get(String(recipe.id))?.status === 'upgraded'
                    return (
                        <Switch
                            className="mb-0"
                            checked={recipe.enabled || false}
                            // MER-3680: a v1 recipe enabled next to its upgraded Segment runs on both engines.
                            disabled={upgraded && !recipe.enabled}
                            onChange={(e: any) => {
                                const enabled = e.target.checked
                                if (enabled && upgraded) return
                                API.Recipes.Edit(recipe.id ?? 0, {
                                    ...recipe,
                                    enabled,
                                })
                                    .then(() => {
                                        Toasts.success(
                                            enabled ? 'Recipe enabled' : 'Recipe disabled',
                                        )
                                        extra.mutateItems((recipes: Recipe[]) =>
                                            recipes.map((r) => {
                                                if (r.id === recipe.id) {
                                                    return {
                                                        ...r,
                                                        enabled,
                                                    }
                                                }
                                                return r
                                            }),
                                        )
                                    })
                                    .catch(toastUpgradedToV2Refusal)
                            }}
                        />
                    )
                },
            },
            {
                key: 'ad_type',
                cell: (recipe) => {
                    return <UpdateReportAdTypeBadge type={recipe.ad_type} />
                },
            },
        ]

        if (asPublicRecipes) {
            c.push({
                key: 'description',
                cell: (recipe) => (
                    <div
                        className="hover:font-underline cursor-pointer"
                        onClick={() => setDescription(recipe)}
                    >
                        {recipe.description || <i>none</i>}
                    </div>
                ),
            })
        }

        c.push(
            {
                key: 'trigger',
                cell: (recipe) => {
                    return (
                        <pre
                            title={recipe.trigger}
                            className="overflow-hidden"
                            style={{ maxHeight: '60px', maxWidth: '500px' }}
                        >
                            {recipe.trigger}
                        </pre>
                    )
                },
            },
            {
                key: 'frequency',
                cell: (recipe) => (
                    <ScheduledSegmentFrequency
                        recipe={recipe as ScheduledSegmentListRecipe}
                        fallback={<FrequencyBadge {...recipe} />}
                    />
                ),
            },
            {
                key: 'action',
                cell: (recipe) => {
                    return (
                        <ActionBadge action={recipe.action} action_params={recipe.action_params} />
                    )
                },
            },
            {
                key: 'last_change',
                cell: (recipe) => (
                    <div>
                        {recipe.last_change == null
                            ? 'Never'
                            : format(new Date(recipe.last_change), 'M/d/yy H:mm a')}
                    </div>
                ),
            },
            {
                key: 'last_run',
                cell: (recipe) => (
                    <div>
                        {recipe.last_run == null
                            ? 'Never'
                            : format(new Date(recipe.last_run), 'M/d/yy H:mm a')}
                    </div>
                ),
            },
            {
                key: 'upgrade',
                width: 120,
                noRowClick: true,
                sortable: false,
                cell: (recipe) => (
                    <RecipeUpgradeCell
                        recipe={recipe}
                        status={upgradeStatuses.get(String(recipe.id))}
                        enabled={!!upgradeSummary && !asPublicRecipes}
                    />
                ),
            },
            {
                key: ' ',
                sortable: false,
                cell: (recipe) => {
                    return (
                        <EllipsisMenu
                            content={
                                <Menu>
                                    {!asPublicRecipes && (
                                        <Popover
                                            interactionKind="hover"
                                            content={
                                                <div className="p-2">
                                                    Cannot run a disabled recipe
                                                </div>
                                            }
                                            disabled={recipe?.enabled}
                                        >
                                            <MenuItem
                                                disabled={
                                                    recipesCurrentlyRunning || !recipe?.enabled
                                                }
                                                text="Run Now"
                                                onClick={() => setRecipe(recipe)}
                                            />
                                        </Popover>
                                    )}
                                    <Popover
                                        interactionKind="hover"
                                        content={
                                            <div className="p-2">
                                                {recipesRetiredCopy.duplicateDisabled}
                                            </div>
                                        }
                                        disabled={asPublicRecipes}
                                    >
                                        <MenuItem
                                            disabled={!asPublicRecipes}
                                            text="Duplicate"
                                            onClick={() => duplicateRecipe({ recipe })}
                                        />
                                    </Popover>
                                    {upgradeRowsById.get(String(recipe.id))?.status ===
                                        'upgraded' && (
                                        <MenuItem
                                            text="View Segment"
                                            onClick={() =>
                                                navigate(
                                                    `/segments/${upgradeRowsById.get(String(recipe.id))?.segment_id ?? ''}`,
                                                )
                                            }
                                        />
                                    )}
                                    <MenuItem
                                        text="Export"
                                        onClick={() => exportRecipe({ recipe })}
                                    />
                                    <MenuItem
                                        text="Delete"
                                        onClick={() => {
                                            if (
                                                confirm(
                                                    `Are you sure you want to delete recipe "${recipe.name}"?`,
                                                )
                                            ) {
                                                deleteRecipe({ recipe })
                                            }
                                        }}
                                    />
                                </Menu>
                            }
                        />
                    )
                },
            },
        )

        return c
    }, [asPublicRecipes, navigate, recipesCurrentlyRunning, upgradeRowsById, upgradeStatuses])

    const tableFilters = useMemo(
        () => recipeFilters(!asPublicRecipes && !!upgradeSummary),
        [asPublicRecipes, upgradeSummary],
    )

    const breadcrumbs: BreadcrumbProps[] = [{ text: 'Automate' }]

    return (
        <Layout
            breadcrumbs={breadcrumbs}
            nested={nested}
            primary={showHeader ? 'Recipes' : null}
            className={useNavV2FlatChrome ? 'bg-white' : undefined}
            layoutClassName={useNavV2FlatChrome ? '!p-0 md:!p-0 bg-white' : undefined}
            extraMenuItems={
                asPublicRecipes
                    ? []
                    : [
                          <MenuItem
                              key="v2-quickstart"
                              icon="help"
                              text="Recipes V2 Quickstart"
                              onClick={() => navigate('/help/recipes-v2-quickstart')}
                          />,
                      ]
            }
        >
            {!asPublicRecipes && <RecipesRetiredNotice />}
            {upgradeSummary && !asPublicRecipes && (
                <RecipeUpgradeBanner summary={upgradeSummary} enabled />
            )}
            <RecipeListAgGrid
                name={asPublicRecipes ? TableName.PublicRecipes : TableName.Recipes}
                store={store}
                savedFiltersStore={savedFiltersStore}
                tableHooks={tableHooks}
                filters={tableFilters}
                columns={columns}
                nested={nested}
                className={classNames(!showHeader && !useNavV2FlatChrome && 'mt-0 mb-4')}
                cardClassName={classNames(
                    'table-header-top-border',
                    useNavV2FlatChrome && '!rounded-none !shadow-none',
                )}
                createAction={
                    asPublicRecipes ? (
                        <Button
                            intent="primary"
                            text="Create Recipe"
                            className="mr-2 rounded-lg whitespace-nowrap"
                            onClick={() => navigate('/admin/public-recipes/new')}
                        />
                    ) : undefined
                }
            />
            <RecipesProgressPreviewDialog
                isOpen={recipesCurrentlyRunning}
                recipes={tableHooks.items}
                data={recipesProgress}
            />
            {/* nonModal: contains a hover Popover that portals to body; modal
                mode disables pointer events on body and breaks the popover. */}
            <Dialog isOpen={!!recipe} onClose={() => setRecipe(null)} className="p-4" nonModal>
                <span>
                    Are you sure you want to run {recipe?.name}? This recipe
                    {recipe?.last_run == null
                        ? ' has never been run'
                        : ' was last run at ' + format(new Date(recipe?.last_run), 'M/d/yy H:mm a')}
                </span>
                <div className="mt-3 flex justify-between">
                    <Button disabled={loading} text="Cancel" onClick={() => setRecipe(null)} />
                    <Popover
                        interactionKind="hover"
                        content={<div className="p-2">Cannot run a disabled recipe</div>}
                        disabled={recipe?.enabled}
                    >
                        <Button
                            disabled={!recipe?.enabled}
                            text="Run Now"
                            loading={loading}
                            intent="primary"
                            onClick={() => {
                                // Use new BA runner
                                let re: Promise<any>
                                if (recipe.version == 2 && recipe.ad_type == 20) {
                                    re = axiosPost(
                                        `api/v5/recipes/${recipe?.id ?? 0}/run`,
                                        null,
                                        true,
                                    )
                                } else {
                                    re = axiosPost(`recipes/${recipe?.id ?? 0}/run`)
                                }
                                setRecipe(null)

                                re.then((res) => {
                                    dispatch(reduxSetRecipesProgress(res.data.progress))
                                }).catch((res) => {
                                    setLoading(false)
                                    Toasts.error(res.message)
                                    console.log('res', res)
                                })
                            }}
                        />
                    </Popover>
                </div>
            </Dialog>
            <DescriptionModal
                description={description}
                setDescription={setDescription}
                reload={() => tableHooks.reload()}
            />
        </Layout>
    )
}

interface DescriptionModalProps {
    description: Recipe | null
    setDescription: Dispatch<SetStateAction<Recipe | null>>
    reload: () => void
}

const DescriptionModal = ({ description, setDescription, reload }: DescriptionModalProps) => {
    const [loading, setLoading] = useState<boolean>(false)
    const [newDescription, setNewDescription] = useState(description?.description)

    useEffect(() => {
        setNewDescription(description?.description)
    }, [description])

    return (
        <Dialog isOpen={!!description} onClose={() => setDescription(null)} className="p-4">
            <TextArea
                autoFocus
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
            />
            <div className="mt-3 flex justify-between">
                <Button disabled={loading} text="Cancel" onClick={() => setDescription(null)} />
                <Button
                    text="Save"
                    loading={loading}
                    intent="primary"
                    onClick={() => {
                        setLoading(true)
                        const p = axiosPatch(
                            `admin/public-recipes/${description?.id}/description`,
                            {
                                description: newDescription,
                            },
                        )

                        p.then(() => {
                            setLoading(false)
                            Toasts.success('Description saved')
                            reload()
                            setDescription(null)
                        }).catch((res) => {
                            setLoading(false)
                            Toasts.error(res.message)
                        })
                    }}
                />
            </div>
        </Dialog>
    )
}

interface RecipesProgressPreviewDialogProps {
    isOpen: boolean
    data: { [key: number]: BulkActionProgress }
    recipes: Models.Recipe[]
}

export const RecipesProgressPreviewDialog = ({
    isOpen,
    data,
    recipes,
}: RecipesProgressPreviewDialogProps) => {
    return (
        <Dialog usePortal={false} isOpen={isOpen} className="p-4">
            <RecipesProgressPreview data={data} recipes={recipes} />
        </Dialog>
    )
}

interface RecipesProgressPreviewProps {
    data: { [key: number]: BulkActionProgress }
    recipes: Models.Recipe[]
}

const RecipesProgressPreview = ({ data, recipes }: RecipesProgressPreviewProps) => {
    const bars = Object.keys(data).map((recipeId) => {
        const recipe = recipes.find((r) => r.id == parseInt(recipeId))
        const name = recipe?.name ?? null
        const progress = data[parseInt(recipeId)]

        return {
            progress,
            title: (
                <span>
                    {name}{' '}
                    <span className="text-sm text-gray-500">
                        {progress.trigger == RecipeTrigger.Manual
                            ? '(Triggered Manually)'
                            : '(Triggered Automatically)'}
                    </span>
                </span>
            ),
        }
    })

    return (
        <ProgressPreview
            bars={bars}
            description={
                <div>
                    Your recipe is currently running. This can take a few minutes for large recipe
                    runs
                </div>
            }
        />
    )
}

export const stripCommentsFromTrigger = (trigger: string): string => {
    trigger = trigger.replace(/\/\/.*$/m, '')
    trigger = trigger.replace(/\/\*[\s\S]*\*\//, '')
    trigger = trigger.trim()
    return trigger
}
